import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Swords, Trophy, ArrowLeft, Calendar } from 'lucide-react';

const BatalhaDePKPublic = () => {
  const [config, setConfig] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [currentWinners, setCurrentWinners] = useState(null);
  const [winnersHistory, setWinnersHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Selecionar última rodada automaticamente
    if (rounds.length > 0 && !selectedRound) {
      setSelectedRound(rounds[rounds.length - 1].round_number);
    }
  }, [rounds]);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadConfig(),
        loadRounds(),
        loadWinners(),
        loadWinnersHistory()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
    setLoading(false);
  };

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const loadRounds = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/rounds`);
      // Filtrar apenas rodadas visíveis
      const visibleRounds = response.data.filter(r => r.show_to_public);
      setRounds(visibleRounds);
    } catch (error) {
      console.error('Erro ao carregar rodadas:', error);
    }
  };

  const loadWinners = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/winners/current-with-data`);
      setCurrentWinners(response.data);
    } catch (error) {
      console.error('Erro ao carregar ganhadores:', error);
    }
  };

  const loadWinnersHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/winners/history`);
      setWinnersHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <Swords className="w-16 h-16 mx-auto text-red-600 animate-pulse mb-4" />
          <p className="text-xl text-gray-600">Carregando BATALHA DE PK...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-8 bg-cover bg-center bg-fixed relative"
      style={{ backgroundImage: 'url(/pk-background.png)' }}
    >
      {/* Overlay semi-transparente para legibilidade */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      <div className="container mx-auto px-2 sm:px-4 max-w-6xl relative z-10">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="text-center mb-4 sm:mb-6 mt-2 sm:mt-4">
            {/* Banner do Evento - Responsivo */}
            <div className="mb-2 sm:mb-4">
              <img 
                src="/pk-banner.png" 
                alt="BATALHA DE PK" 
                className="max-w-full h-auto mx-auto animate-heartbeat"
                style={{ maxHeight: '300px', maxWidth: '100%' }}
              />
            </div>
            <p className="text-lg sm:text-2xl font-bold text-white drop-shadow-lg px-4">Torneio Eliminatório Épico</p>
          </div>
        </div>

        {/* Calendário de Rodadas - PRIMEIRO */}
        {rounds.length > 0 && (
          <Card className="mb-8 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-red-500 rounded-t-lg">
              {/* Imagem Rounds */}
              <div className="text-center">
                <img 
                  src="/pk-rounds.png" 
                  alt="ROUNDS" 
                  className="max-w-full h-auto mx-auto"
                  style={{ maxHeight: '150px' }}
                />
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {/* Menu Dropdown de Rodadas */}
              <div className="mb-4">
                <select 
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 bg-white font-semibold text-base focus:border-red-500 focus:ring-2 focus:ring-red-200"
                  value={selectedRound || ''}
                  onChange={(e) => setSelectedRound(parseInt(e.target.value))}
                >
                  {rounds.map((round) => (
                    <option key={round.round_number} value={round.round_number}>
                      🎮 Rodada {round.round_number} {round.round_datetime && `- ${round.round_datetime}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Matches da Rodada Selecionada */}
              {selectedRound && rounds.find(r => r.round_number === selectedRound)?.matches.map((match, idx, arr) => {
                          // Card de BYE Compacto
                          if (match.is_bye) {
                            const byeData = match.bye_participant_data;
                            return (
                              <React.Fragment key={idx}>
                                <div className="bg-green-600 rounded-lg p-3 flex items-center gap-3">
                                  {byeData?.avatar_url && (
                                    <img src={byeData.avatar_url} alt="" className="w-12 h-12 rounded-full border-2 border-white" />
                                  )}
                                  <div className="flex-1">
                                    <p className="font-bold text-white">{byeData?.nickname || 'Desconhecido'}</p>
                                    <p className="text-xs text-green-100">✅ Classificado Automaticamente (BYE)</p>
                                  </div>
                                </div>
                                {/* Separador entre cards */}
                                {idx < arr.length - 1 && (
                                  <div className="my-4">
                                    <img src="/pk-divider.png" alt="" className="w-full h-auto opacity-80" />
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          }

                          // Card de Batalha Normal - Visual Épico
                          const p1Data = match.participant1_data;
                          const p2Data = match.participant2_data;
                          const totalPoints = (match.points_participant1 || 0) + (match.points_participant2 || 0);
                          
                          // Calcular porcentagem
                          let p1Percentage = 50;
                          let p2Percentage = 50;
                          
                          if (totalPoints > 0) {
                            const p1Raw = (match.points_participant1 / totalPoints) * 100;
                            const p2Raw = (match.points_participant2 / totalPoints) * 100;
                            
                            if (match.points_participant1 > 0 && p1Raw < 1) {
                              p1Percentage = 1;
                              p2Percentage = 99;
                            } else if (match.points_participant2 > 0 && p2Raw < 1) {
                              p1Percentage = 99;
                              p2Percentage = 1;
                            } else if (match.points_participant1 !== match.points_participant2) {
                              p1Percentage = p1Raw;
                              p2Percentage = p2Raw;
                            }
                          }
                          
                          const isFinalized = match.status === 'finalizado' || 
                                              match.status === 'wo' || 
                                              match.status === 'wo_both_disqualified' || 
                                              match.status === 'both_advance';

                          return (
                            <React.Fragment key={idx}>
                              <div className="border-2 rounded-lg overflow-hidden border-gray-300">
                              {/* Horário e Status no topo */}
                              <div className="bg-gray-100 py-2 px-4 text-center border-b space-y-1">
                                {match.battle_datetime && (
                                  <p className="text-sm font-semibold text-gray-700">⏰ {match.battle_datetime}</p>
                                )}
                                <Badge
                                  className={
                                    match.status === 'finalizado'
                                      ? 'bg-green-600'
                                      : match.status === 'wo'
                                      ? 'bg-yellow-600'
                                      : match.status === 'wo_both_disqualified'
                                      ? 'bg-gray-600'
                                      : match.status === 'both_advance'
                                      ? 'bg-blue-600'
                                      : 'bg-gray-500'
                                  }
                                >
                                  {match.status === 'finalizado' 
                                    ? '✅ Finalizado' 
                                    : match.status === 'wo' 
                                    ? '⚠️ WO' 
                                    : match.status === 'wo_both_disqualified'
                                    ? '🚫 WO - Ambos Desclassificados'
                                    : match.status === 'both_advance'
                                    ? '🤝 Ambos Classificados'
                                    : '⏳ Aguardando'}
                                </Badge>
                              </div>
                              
                              {/* Card dividido em duas colunas */}
                              <div className="grid grid-cols-2 relative">
                                {/* Imagem VS no centro */}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                  <img src="/vs-icon.png" alt="VS" className="w-20 h-20 drop-shadow-lg" />
                                </div>

                                {/* TIME AZUL */}
                                <div 
                                  className="bg-blue-50 p-3 bg-cover bg-center relative"
                                  style={{ backgroundImage: 'url(/bg-blue.png)' }}
                                >
                                  <div className="absolute inset-0 bg-blue-50/70"></div>
                                  
                                  <div className="relative z-10 flex flex-col items-center gap-2">
                                    <div className="relative w-32 h-32 flex items-center justify-center">
                                      {p1Data?.avatar_url && (
                                        <img src={p1Data.avatar_url} alt="" className="w-[98px] h-[98px] rounded-full border-2 border-blue-400" />
                                      )}
                                      {isFinalized && (match.winner_kako_id === p1Data?.kako_id || match.status === 'both_advance') && (
                                        <img src="/frame-winner.png" alt="Ganhou" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                      )}
                                      {isFinalized && match.winner_kako_id !== p1Data?.kako_id && match.status !== 'both_advance' && match.status !== 'wo_both_disqualified' && (
                                        <img src="/frame-loser.png" alt="Perdeu" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                      )}
                                    </div>
                                    <div className="text-center">
                                      <p className="font-bold text-blue-700">{p1Data?.nickname || 'Desconhecido'}</p>
                                      {(match.status === 'wo' || match.status === 'wo_both_disqualified' || match.status === 'both_advance') && (
                                        <Badge className="bg-yellow-600 text-xs mt-1">⚠️ W.O</Badge>
                                      )}
                                      <p className="text-xs text-gray-600 mt-1">#{p1Data?.kako_id}</p>
                                    </div>
                                  </div>
                                </div>

                                {/* TIME VERMELHO */}
                                <div 
                                  className="bg-red-50 p-3 bg-cover bg-center relative"
                                  style={{ backgroundImage: 'url(/bg-red.png)' }}
                                >
                                  <div className="absolute inset-0 bg-red-50/70"></div>
                                  
                                  <div className="relative z-10 flex flex-col items-center gap-2">
                                    <div className="relative w-32 h-32 flex items-center justify-center">
                                      {p2Data?.avatar_url && (
                                        <img src={p2Data.avatar_url} alt="" className="w-[98px] h-[98px] rounded-full border-2 border-red-400" />
                                      )}
                                      {isFinalized && (match.winner_kako_id === p2Data?.kako_id || match.status === 'both_advance') && (
                                        <img src="/frame-winner.png" alt="Ganhou" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                      )}
                                      {isFinalized && match.winner_kako_id !== p2Data?.kako_id && match.status !== 'both_advance' && match.status !== 'wo_both_disqualified' && (
                                        <img src="/frame-loser.png" alt="Perdeu" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                      )}
                                    </div>
                                    <div className="text-center">
                                      <p className="font-bold text-red-700">{p2Data?.nickname || 'Desconhecido'}</p>
                                      {(match.status === 'wo' || match.status === 'wo_both_disqualified' || match.status === 'both_advance') && (
                                        <Badge className="bg-yellow-600 text-xs mt-1">⚠️ W.O</Badge>
                                      )}
                                      <p className="text-xs text-gray-600 mt-1">#{p2Data?.kako_id}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Barra Visual - Só aparece quando finalizado NORMAL */}
                              {match.status === 'finalizado' && (
                                <div className="bg-gray-100 p-2 relative">
                                  <div className="flex items-center justify-between gap-2 text-base font-bold mb-1">
                                    <span className="text-blue-600">{match.points_participant1}</span>
                                    <span className="text-red-600">{match.points_participant2}</span>
                                  </div>
                                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
                                    <span className="text-gray-600 font-bold text-lg">×</span>
                                  </div>
                                  <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden flex">
                                    <div
                                      className="bg-blue-500 flex items-center justify-center text-white text-xs font-bold"
                                      style={{ width: `${p1Percentage}%` }}
                                    >
                                      {p1Percentage > 15 && `${Math.round(p1Percentage)}%`}
                                    </div>
                                    <div
                                      className="bg-red-500 flex items-center justify-center text-white text-xs font-bold"
                                      style={{ width: `${p2Percentage}%` }}
                                    >
                                      {p2Percentage > 15 && `${Math.round(p2Percentage)}%`}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Separador entre cards */}
                            {idx < arr.length - 1 && (
                              <div className="my-4">
                                <img src="/pk-divider.png" alt="" className="w-full h-auto opacity-80" />
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
            </CardContent>
          </Card>
        )}

        {/* Regras do Evento - DEPOIS DE ROUNDS */}
        {config?.rules_text && (
          <Card className="mb-8 border-2 overflow-hidden border-purple-300">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-red-500 rounded-t-lg">
              {/* Imagem Regras */}
              <div className="text-center">
                <img 
                  src="/pk-regras.png" 
                  alt="REGRAS" 
                  className="max-w-full h-auto mx-auto"
                  style={{ maxHeight: '150px' }}
                />
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {config.rules_text}
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Ganhadores Atuais */}
        {currentWinners && (
          <Card className="mb-8 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-red-500 rounded-t-lg">
              {/* Imagem Ganhadores */}
              <div className="text-center">
                <img 
                  src="/pk-ganhadores.png" 
                  alt="GANHADORES" 
                  className="max-w-full h-auto mx-auto"
                  style={{ maxHeight: '150px' }}
                />
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Campeão */}
                <Card className="border-4 border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100">
                  <CardContent className="p-4 sm:p-6 text-center space-y-3">
                    <h3 className="text-2xl sm:text-3xl font-bold text-yellow-700">🥇 CAMPEÃO</h3>
                    <div className="relative w-[200px] sm:w-[280px] h-[200px] sm:h-[280px] mx-auto flex items-center justify-center">
                      {currentWinners.top1?.avatar && (
                        <img src={currentWinners.top1.avatar} alt="" className="w-32 sm:w-40 h-32 sm:h-40 rounded-full border-4 border-yellow-500" />
                      )}
                      <img src="/frame-champion.png" alt="Campeão" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                    </div>
                    <p className="text-xl sm:text-2xl font-bold">{currentWinners.top1?.nickname || 'Desconhecido'}</p>
                  </CardContent>
                </Card>

                {/* Vice */}
                {currentWinners.top2 && (
                  <Card className="border-4 border-gray-400 bg-gradient-to-br from-gray-50 to-gray-100">
                    <CardContent className="p-4 sm:p-6 text-center space-y-3">
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-700">🥈 VICE-CAMPEÃO</h3>
                      <div className="relative w-[200px] sm:w-[280px] h-[200px] sm:h-[280px] mx-auto flex items-center justify-center">
                        {currentWinners.top2?.avatar && (
                          <img src={currentWinners.top2.avatar} alt="" className="w-32 sm:w-40 h-32 sm:h-40 rounded-full border-4 border-gray-500" />
                        )}
                        <img src="/frame-vice.png" alt="Vice" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                      </div>
                      <p className="text-lg sm:text-xl font-bold">{currentWinners.top2?.nickname || 'Desconhecido'}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </CardContent>
          </Card>
        )}


        {/* Premiação em Destaque - DEPOIS DE GANHADORES */}
        {(config?.prize_top1 || config?.prize_top2) && (
          <Card className="mb-8 border-4 overflow-hidden border-yellow-400 bg-gradient-to-r from-blue-500 to-red-500">
            <CardContent className="p-6">
              {/* Imagem Premiação */}
              <div className="mb-6">
                <img 
                  src="/pk-premiacao.png" 
                  alt="PREMIAÇÃO" 
                  className="max-w-full h-auto mx-auto"
                  style={{ maxHeight: '200px' }}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {config.prize_top1 && (
                  <div className="text-center p-4 sm:p-6 bg-yellow-200 rounded-lg shadow-lg">
                    <p className="text-base sm:text-lg font-semibold text-yellow-800 mb-2">🥇 1º LUGAR</p>
                    <p className="text-2xl sm:text-4xl font-bold text-yellow-900">{config.prize_top1}</p>
                  </div>
                )}
                {config.prize_top2 && (
                  <div className="text-center p-4 sm:p-6 bg-gray-200 rounded-lg shadow-lg">
                    <p className="text-base sm:text-lg font-semibold text-gray-700 mb-2">🥈 2º LUGAR</p>
                    <p className="text-xl sm:text-3xl font-bold text-gray-800">{config.prize_top2}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Botão para Participar */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/')}
            size="lg"
            className="bg-gradient-to-r from-red-600 to-orange-600 text-white text-xl px-12 py-6 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
          >
            ⚔️ PARTICIPAR DO EVENTO ⚔️
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BatalhaDePKPublic;
